import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// One shared axios instance that sends cookies + a bearer fallback for when third-party
// cookie contexts strip them. We keep the token in memory (and localStorage) for that fallback.
export const authClient = axios.create({ baseURL: API, withCredentials: true, timeout: 15000 });

const TOKEN_KEY = "lostdc:token";

authClient.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers = { ...(config.headers || {}), Authorization: `Bearer ${t}` };
  return config;
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      // First try the current access token/cookie.
      const { data } = await authClient.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch (err) {
      // Access token may have expired. Use the long-lived refresh cookie.
      try {
        const { data } = await authClient.post("/auth/refresh");

        if (data?.token) {
          localStorage.setItem(TOKEN_KEY, data.token);
        }

        // Re-check the authenticated user with the new access token.
        const me = await authClient.get("/auth/me");
        setUser(me.data.user);
        return me.data.user;
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
        return null;
      }
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && (window.location.hash || "").includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await authClient.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await authClient.post("/auth/register", payload);
    if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await authClient.post("/auth/logout"); } catch (err) { console.debug("server logout failed (ok, client-side cleared)", err?.message); }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    // Hard navigation to home to guarantee protected routes re-guard
    if (typeof window !== "undefined") window.location.assign("/");
  };

  const updateProfile = async (patch) => {
    const { data } = await authClient.put("/auth/profile", patch);
    setUser(data.user);
    return data.user;
  };

  const updatePrivacy = async (patch) => {
    const { data } = await authClient.put("/auth/privacy", patch);
    setUser(data.user);
    return data.user;
  };

  const linkMinecraft = async (username) => {
    const { data } = await authClient.post("/auth/link-minecraft", { username });
    setUser(data.user);
    return data.user;
  };

  const unlinkMinecraft = async () => {
    const { data } = await authClient.delete("/auth/unlink-minecraft");
    setUser(data.user);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, updateProfile, updatePrivacy, linkMinecraft, unlinkMinecraft }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}
